import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, formatDateTR } from "../lib/apiClient";
import { PageHeader, ActionButton } from "../components/Layout";
import { PlusCircle, ArrowRight, Truck, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth, hasRole } from "../lib/auth";

const STATUS_LABEL = { HAZIRLANIYOR: "Hazırlanıyor", TAMAMLANDI: "Tamamlandı" };

export default function ShipmentsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { user } = useAuth();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shipments");
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const canDelete = hasRole(user, "production_manager");

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/shipments/${deleteTarget.id}`);
      toast.success(`${deleteTarget.shipment_number} silindi`);
      setDeleteTarget(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setDeleting(false);
  };

  return (
    <div>
      <PageHeader
        title="Sevkiyatlar"
        subtitle="Yükleme ve gönderim yönetimi"
        actions={
          hasRole(user, "production_manager", "shipping_operator") && (
            <Link to="/sevkiyatlar/yeni">
              <ActionButton data-testid="new-shipment-btn"><PlusCircle size={18}/> Yeni Sevkiyat</ActionButton>
            </Link>
          )
        }
      />

      <div className="card-industrial overflow-x-auto">
        <table className="w-full text-sm" data-testid="shipments-table">
          <thead>
            <tr className="bg-slate-100 border-b border-slate-300 text-xs uppercase tracking-widest text-slate-600">
              <th className="text-left px-4 py-3">Sevkiyat No</th>
              <th className="text-left px-4 py-3">Müşteri</th>
              <th className="text-left px-4 py-3">Varış</th>
              <th className="text-left px-4 py-3">Sevkiyat Tarihi</th>
              <th className="text-left px-4 py-3">Araç</th>
              <th className="text-right px-4 py-3">Beklenen</th>
              <th className="text-right px-4 py-3">Okutulan</th>
              <th className="text-right px-4 py-3">Kalan</th>
              <th className="text-left px-4 py-3">Durum</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="p-8 text-center text-slate-500">Yükleniyor...</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={10} className="p-10 text-center text-slate-500">
                <Truck size={40} className="mx-auto mb-2 opacity-40" />
                Henüz sevkiyat oluşturulmadı.
              </td></tr>
            )}
            {rows.map((s, i) => (
              <tr key={s.id} className={`border-b border-slate-200 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"} hover:bg-blue-50`} data-testid={`shipment-row-${s.shipment_number}`}>
                <td className="px-4 py-3 font-mono font-bold">{s.shipment_number}</td>
                <td className="px-4 py-3 font-semibold">{s.customer_name}</td>
                <td className="px-4 py-3">{s.destination}</td>
                <td className="px-4 py-3 font-mono tabular-nums">{formatDateTR(s.shipment_date)}</td>
                <td className="px-4 py-3 text-slate-600">{s.vehicle_info || "-"}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums">{s.expected}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-emerald-700 font-bold">{s.scanned}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-red-700 font-bold">{s.remaining}</td>
                <td className="px-4 py-3">
                  <span className={`chip ${s.status === "TAMAMLANDI" ? "chip-sevk" : "chip-bekliyor"}`}>{STATUS_LABEL[s.status] || s.status}</span>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Link to={`/sevkiyatlar/${s.id}`} className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 font-bold text-xs mr-2">
                    Detay <ArrowRight size={14} />
                  </Link>
                  {canDelete && (
                    <button onClick={() => setDeleteTarget(s)}
                      data-testid={`shipment-delete-${s.shipment_number}`}
                      title="Sevkiyatı Sil"
                      className="inline-flex items-center justify-center w-7 h-7 border border-red-300 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600">
                      <Trash2 size={14}/>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {deleteTarget && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => !deleting && setDeleteTarget(null)} data-testid="ship-delete-modal">
          <div className="bg-white border-2 border-red-700 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-red-600 text-white grid place-items-center shrink-0"><Trash2 size={22}/></div>
              <div className="flex-1">
                <h3 className="font-display font-black text-xl">Sevkiyatı Sil</h3>
                <p className="text-sm text-slate-600 mt-1">
                  <b className="font-mono">{deleteTarget.shipment_number}</b> silinecek. İçindeki <b>{deleteTarget.expected}</b> parça <b>HAZIR</b> durumuna geri döner. Bu işlem geri alınamaz.
                </p>
              </div>
              <button onClick={() => !deleting && setDeleteTarget(null)} className="text-slate-400 hover:text-slate-900"><X size={20}/></button>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setDeleteTarget(null)} disabled={deleting}
                data-testid="ship-delete-cancel"
                className="px-5 py-2 border-2 border-slate-300 hover:border-slate-800 font-bold text-sm">İptal</button>
              <button onClick={confirmDelete} disabled={deleting}
                data-testid="ship-delete-confirm"
                className="inline-flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-bold uppercase text-sm">
                <Trash2 size={16}/> {deleting ? "Siliniyor..." : "Evet, Sil"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
